// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { css, keyframes } from '@emotion/react';
import { Flex, Box, useSystem } from 'react-system';
import { format, parse } from 'date-fns';
import { Card, Button } from '@material-ui/core';
import { Carousel } from '@realadvisor/carousel';
import { Image } from '@realadvisor/image';
import { useTheme } from '../../theme';
import { useLocale } from '../../controls/locale';
import { Link } from '../../controls/link';
import * as icons from '../../icons';
import type { DevelopmentSummary_development$key } from './__generated__/DevelopmentSummary_development.graphql';

type Props = {|
  development: DevelopmentSummary_development$key,
|};

const textLoadingClassname = 'textLoadingClassname';
const imageLoadingInvisibleClassname = 'imageLoadingInvisibleClassname';
const buttonLoadingDisabledClassname = 'buttonLoadingDisabledClassname';
const smallIconDisabledClassname = 'smallIconDisabledClassname';

export const useSummaryLoadingStyle = (): any => {
  // const theme = useTheme();

  const placeHolderShimmer = keyframes`
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  `;

  const animationBg = css`
    animation-duration: 1s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: ${placeHolderShimmer};
    animation-timing-function: linear;
    background: #f6f7f8;
    background: linear-gradient(to right, #eeeeee 8%, #dddddd 18%, #eeeeee 33%);
    background-size: 800px 104px;
  `;

  return css`
    .${textLoadingClassname} {
      color: rgba(255, 0, 0, 0);
      margin-top: 0.3em;
      margin-bottom: 0.3em;
      line-height: 1em;

      ${animationBg}
    }

    .${imageLoadingInvisibleClassname} {
      opacity: 0;
    }

    .${buttonLoadingDisabledClassname} {
      pointer-events: none;
      transition: none;
      color: rgba(0, 0, 0, 0);
      box-shadow: none;
      background-color: rgba(0, 0, 0, 0.12);

      ${animationBg}
    }

    .${smallIconDisabledClassname} {
      width: 16px;
      height: 16px;
      background-color: #eee;
      svg {
        display: none;
      }
      ${animationBg}
    }
  `;
};

const aspectRatio = props => css`
  flex: 1;
  position: relative;
  &:before {
    width: 100%;
    padding-top: ${100 * (props.ratio ?? 1)}%;
    content: '';
  }
`;

const SmallIcon = ({ className, children }) => {
  return (
    <div
      css={{
        display: 'inline-block',
        marginRight: 6,
      }}
      className={className}
    >
      {children}
    </div>
  );
};

const FeatureTitle = ({ className, children }) => {
  return (
    <div
      css={{
        color: '#777777',
        fontSize: 16,
        fontWeight: 600,
        lineHeight: '22px',
      }}
      className={className}
    >
      {children}
    </div>
  );
};

export const DevelopmentSummary: React.AbstractComponent<Props, mixed> =
  React.memo(props => {
    const { text, colors } = useTheme();
    const { media } = useSystem();
    const imageRatio = 0.55;
    const { t, dateLocale, numberLocale, currency } = useLocale();

    const development = useFragment(
      graphql`
        fragment DevelopmentSummary_development on DevelopmentProduct {
          title
          deliveryDate
          slug
          developer {
            name
          }
          broker {
            organisation {
              name
            }
          }
          developmentImages {
            size
            crop
            displayOnListing
            backgroundColor
            image {
              url
            }
          }
          aggregates {
            productsCount
            activeProductsCount
            minSalePrice
            minNumberOfBedrooms
            maxNumberOfBedrooms
            postcode
            locality
          }
        }
      `,
      props.development,
    );

    const { title, aggregates, broker, developer, slug, developmentImages } =
      development;

    const images = (developmentImages || [])
      .filter(x => x.displayOnListing)
      .map(({ image, size, crop, backgroundColor }) => {
        const [bucketName, fileName] = image.url.split('/').slice(3, 5);
        return { bucketName, fileName, size, crop, backgroundColor };
      });

    const formatCurrency = val =>
      numberLocale.format(val, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });

    const deliveryDate =
      development.deliveryDate != null
        ? format(
            parse(development.deliveryDate, 'yyyy-MM-dd', new Date()),
            'MMMM yyyy',
            {
              locale: dateLocale,
            },
          )
        : null;

    return (
      <Card
        css={{
          // backgroundColor: colors.primary.main,
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
        elevation={8}
      >
        <Flex alignItems="stretch" flexWrap="wrap" height="100%">
          <Flex
            flexDirection="column"
            justifyContent="space-between"
            width="100%"
          >
            <Box width={[1]}>
              <Carousel
                media={{
                  gap: 0,
                  itemsOnScreen: 1,
                  aspectRatio: imageRatio,
                }}
              >
                {images.map((item, index) => {
                  return (
                    <Image
                      key={index}
                      src={{
                        bucketName: item.bucketName,
                        fileName: item.fileName,
                      }}
                      srcSize={[[375, 375 * imageRatio]]}
                      options={{
                        c: item.crop,
                        b: item.backgroundColor,
                      }}
                      objectFit={item.crop === 'fit' ? 'contain' : 'cover'}
                      alt={`${title ?? t('realEstateCapital')}, ${t(
                        'developments_promotionProperty',
                      )} — ${[aggregates.postcode, aggregates.locality]
                        .filter(Boolean)
                        .join(' ')}`}
                    />
                  );
                })}
              </Carousel>
              <div css={aspectRatio({ ratio: imageRatio })}>
                <div
                  css={{
                    display: 'inline-block',
                    background:
                      aggregates.activeProductsCount > 0
                        ? '#FBE8D6'
                        : '#DDDDDD',
                    padding: '10px 15px',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '20px',
                    color:
                      aggregates.activeProductsCount > 0
                        ? '#2A2A2A'
                        : '#777777',
                    borderRadius: '5px',
                    zIndex: 1,
                    position: 'absolute',
                    bottom: '32px',
                    right: '16px',
                  }}
                  className={buttonLoadingDisabledClassname}
                >
                  {t('productsAvailableCount', {
                    count: aggregates.activeProductsCount,
                  })}
                </div>
              </div>
            </Box>

            <Link
              href={{
                pathname: `/development`,
                query: {
                  slug,
                },
              }}
            >
              <Box
                width={[1]}
                p={3}
                as={'a'}
                target="_blank"
                css={{
                  color: 'unset',
                  textDecoration: 'none',
                }}
              >
                <h5 css={text.h5} className={textLoadingClassname}>
                  {title}
                </h5>
                <div
                  css={[
                    text.subtitle2,
                    {
                      color: colors.grey600,
                    },
                  ]}
                  className={textLoadingClassname}
                >
                  {aggregates.postcode} {aggregates.locality}
                </div>

                <Flex mt={'20px'}>
                  <Box
                    pr={2}
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <SmallIcon className={smallIconDisabledClassname}>
                      <icons.Apartment size={16} />
                    </SmallIcon>

                    <FeatureTitle className={textLoadingClassname}>
                      {t('productsCount', {
                        count: aggregates.productsCount,
                      })}
                    </FeatureTitle>
                  </Box>
                  <Box
                    pr={2}
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <SmallIcon className={smallIconDisabledClassname}>
                      <icons.Bed size={16} />
                    </SmallIcon>
                    <FeatureTitle className={textLoadingClassname}>
                      {aggregates.minNumberOfBedrooms ===
                      aggregates.maxNumberOfBedrooms
                        ? t('bedsCount', {
                            count: aggregates.minNumberOfBedrooms,
                          })
                        : t('bedsRangeCount', {
                            from: aggregates.minNumberOfBedrooms,
                            to: aggregates.maxNumberOfBedrooms,
                          })}
                    </FeatureTitle>
                  </Box>
                </Flex>

                <Flex mt={1}>
                  <Box
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      visibility: deliveryDate == null ? 'hidden' : 'visible',
                    }}
                  >
                    <SmallIcon className={smallIconDisabledClassname}>
                      <icons.Date size={16} />
                    </SmallIcon>
                    <FeatureTitle className={textLoadingClassname}>
                      {deliveryDate}
                    </FeatureTitle>
                  </Box>
                </Flex>

                <Flex mt={'20px'}>
                  {developer && (
                    <Box width={1 / 2}>
                      <div
                        css={[text.subtitle2, { color: colors.grey600 }]}
                        className={textLoadingClassname}
                      >
                        {t('developer')}
                      </div>
                      <div
                        css={text.subtitle1}
                        className={textLoadingClassname}
                      >
                        {developer.name}
                      </div>
                    </Box>
                  )}
                  {broker?.organisation?.name != null && (
                    <Box width={1 / 2}>
                      <div
                        css={[text.subtitle2, { color: colors.grey600 }]}
                        className={textLoadingClassname}
                      >
                        {t('broker')}
                      </div>
                      <div
                        css={text.subtitle1}
                        className={textLoadingClassname}
                      >
                        {broker.organisation?.name}
                      </div>
                    </Box>
                  )}
                </Flex>
              </Box>
            </Link>
          </Flex>

          <Flex alignItems="flex-end" width="100%">
            <Box
              width={[1, 1 / 2]}
              pl={3}
              pb={3}
              css={media({
                display: ['none', 'block'],
              })}
            >
              <div
                css={[
                  text.subtitle2,
                  {
                    color: colors.grey600,
                  },
                ]}
                className={textLoadingClassname}
              >
                {t('pricesStartAt')}
              </div>
              <div css={text.h6} className={textLoadingClassname}>
                {formatCurrency(aggregates.minSalePrice)}
              </div>
            </Box>

            <Box
              width={1}
              pl={3}
              pr={3}
              pb={3}
              css={media({
                display: ['block', 'none'],
              })}
            >
              <Link
                href={{
                  pathname: `/development`,
                  query: {
                    slug,
                  },
                }}
              >
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  fullWidth={true}
                  component="a"
                  target="_blank"
                  css={{
                    paddingLeft: '35px',
                  }}
                  className={buttonLoadingDisabledClassname}
                >
                  <Flex alignItems="center" flexGrow={1}>
                    <Box flexGrow={1}>
                      <div
                        css={[text.subtitle2]}
                        className={textLoadingClassname}
                      >
                        {t('pricesStartAt')}
                      </div>
                      <div css={text.h6} className={textLoadingClassname}>
                        {formatCurrency(aggregates.minSalePrice)}
                      </div>
                    </Box>
                    <icons.ArrowForward size={24} />
                  </Flex>
                </Button>
              </Link>
            </Box>

            <Box
              pr={3}
              pb={3}
              css={media({
                width: '55%',
                display: ['none', 'block'],
              })}
            >
              <Link
                href={{
                  pathname: `/development`,
                  query: {
                    slug,
                  },
                }}
              >
                <Button
                  color="primary"
                  size="medium"
                  fullWidth={false}
                  css={{
                    paddingLeft: '35px',
                    marginLeft: 'auto',
                    display: 'block',
                  }}
                  className={buttonLoadingDisabledClassname}
                  component="a"
                  target="_blank"
                >
                  <Flex alignItems="center">
                    <Box flexGrow={1}>{t('seeMore')}</Box>
                    <icons.ArrowForward size={24} />
                  </Flex>
                </Button>
              </Link>
            </Box>
          </Flex>
        </Flex>
      </Card>
    );
  });
